import { DateTime } from 'luxon';
import { id } from '../../utils';
import { match } from 'ts-pattern';

/**
 * The timezone of the server. This is used to convert timestamps from the API
 * to the user's local time. Defaults to Australia/Sydney as that is the
 * timezone our servers operate in. This can be overridden by setting the
 * `SERVER_TIMEZONE` environment variable at build time.
 *
 * @constant {string} SERVER_TIMEZONE
 * @default 'Australia/Sydney'
 */
export const SERVER_TIMEZONE = process.env.SERVER_TIMEZONE ?? 'Australia/Sydney';

/**
 * The timezone of the user's browser.
 * @constant {string} LOCAL_TIMEZONE
 */
export const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * The available timezones for the user to select from. These must all be
 * either IANA timezones or the special value 'utc' for cross-compatibility
 * between the Intl API, luxon (used for timezone conversion and formatting TZ
 * strings), and the date-fns library (used for formatting dates and times in
 * input and display components).
 */
export const validTimezones = [
  { id: LOCAL_TIMEZONE, label: `Browser default (${LOCAL_TIMEZONE.replace(/_/g, ' ')})` },
  { id: SERVER_TIMEZONE, label: `System timezone (${SERVER_TIMEZONE.replace(/_/g, ' ')})` },
  { id: 'utc', label: 'UTC' },
  ...Intl.supportedValuesOf('timeZone')
    .map((tz) => ({ id: tz, label: tz.replace(/_/g, ' ') }))
    .filter((tz) => tz.id !== SERVER_TIMEZONE && tz.id !== LOCAL_TIMEZONE),
];

/**
 * The available date formats for the user to select from. These formats must
 * be compatible with the date-fns library.
 *
 * @see https://date-fns.org/v4.1.0/docs/format
 */
export const supportedDateFormats = [
  { id: 'dd/MM/y', label: 'AU/UK Format' },
  { id: 'MM/dd/y', label: 'US Format' },
  { id: 'd. M. y', label: 'CZ/SK Format' },
  { id: 'y‑MM‑dd', label: 'ISO-8601 Date' },
];

/**
 * The available time formats for the user to select from. These formats must
 * be compatible with the date-fns library.
 *
 * @see https://date-fns.org/v4.1.0/docs/format
 */
export const supportedTimeFormats = [
  { id: 'HH:mm:ss', label: '24-hour' },
  { id: 'h:mm:ss a', label: '12-hour' },
];

/**
 * The available timezone formats for the user to select from. These formats
 * must be compatible with the luxon library. The reason why we don't just use
 * the date-fns to format everything is that the date-fns library currently
 * does not support outputting anything beyond ISO offsets and GMT offsets. As
 * such, we're using luxon to format the timezone strings (in addition to doing
 * timezone conversions).
 *
 * @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */
export const supportedTimezoneFormats = [
  { id: 'ZZZZ', label: 'Short Timezone Name' },
  { id: 'ZZZZZ', label: 'Full Timezone Name' },
  { id: 'ZZ', label: 'UTC Offset' },
  { id: 'z', label: 'IANA Timezone Name' },
  { id: '', label: 'Not shown' },
];

export type TimePrecision = 'millisecond' | 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';

/**
 * Sets the precision of a DateTime object to the given value. For example, if
 * the precision is set to 'day', then the time will be truncated to the start
 * of the day (e.g. 14:30:15.123 -> 00:00:00.000).
 */
export function setPrecision(time: DateTime, precision: TimePrecision = 'millisecond'): DateTime {
  return match(precision)
    .with('millisecond', () => time)
    .with('second', () => time.set({ millisecond: 0 }))
    .with('minute', () => time.set({ millisecond: 0, second: 0 }))
    .with('hour', () => time.set({ millisecond: 0, second: 0, minute: 0 }))
    .with('day', () => time.set({ millisecond: 0, second: 0, minute: 0, hour: 0 }))
    .with('month', () => time.set({ millisecond: 0, second: 0, minute: 0, hour: 0, day: 1 }))
    .with('year', () =>
      time.set({ millisecond: 0, second: 0, minute: 0, hour: 0, day: 1, month: 1 }),
    )
    .exhaustive();
}

export interface FormatOptions {
  ignoreTimezoneInfo?: boolean;
  precision?: TimePrecision;
  serverTimezone?: string;
  userFormatFn?: (value: string) => string;
  userTimezone?: string;
}

/**
 * Converts an ISO-8601 string from the server into a JS Date object for form
 * state. If the string has no timezone information (as is the case with many
 * date/time strings from the API), it is assumed to be in `SERVER_TIMEZONE`.
 *
 * If the date/time string has timezone information, but the timezone
 * information is known to be inaccurate (e.g. the API is returning a string
 * with a 'Z' timezone designator, but the time is actually in the
 * `SERVER_TIMEZONE`), then the user should provide a `userFormatFn` that
 * removes the timezone information from the string so that it is treated as a
 * `SERVER_TIMEZONE` time.
 *
 * This function accounts for the `ignoreTimezoneInfo` flag:
 *
 * A. If `ignoreTimezoneInfo` is **false** (or undefined):
 *    1. Parse the input string using Luxon DateTime. If it does not contain
 *       timezone information, assume it is in `SERVER_TIMEZONE`.
 *       - Example: `"2025-01-01T00:00:00.000"` is parsed as `DateTime 2025-01-01T00:00:00+11:00`.
 *       - Example: `"2024-12-31T13:00:00.000Z"` is parsed as `DateTime 2024-12-31T13:00:00Z`.
 *    2. Convert to a JS Date, which adjusts to the user's system timezone.
 *       - Example: If system timezone is `America/Chicago` (UTC-6), the
 *         JS Date would be `Date 2024-12-31T07:00:00-06:00`.
 *
 * B. If `ignoreTimezoneInfo` is **true**:
 *    1. Parse the input string using Luxon DateTime. If it does not contain
 *       timezone information, assume it is in `SERVER_TIMEZONE`.
 *       - Example: `"2025-01-01T00:00:00.000"` is parsed as `DateTime 2025-01-01T00:00:00+11:00`.
 *       - Example: `"2024-12-31T13:00:00.000Z"` is parsed as `DateTime 2024-12-31T13:00:00Z`.
 *    2. Convert to `timezone`, **without shifting time**.
 *       - Example: If `timezone = America/Chicago` (UTC-6), the resulting
 *         DateTime would be `2025-01-01T00:00:00-06:00`.
 *    3. Convert to a JS Date, preserving the displayed time.
 *       - Example: `Date 2025-01-01T00:00:00-06:00` (same clock time, but now
 *         in user's preferred timezone).
 */
export function format({
  ignoreTimezoneInfo = false,
  precision,
  serverTimezone = SERVER_TIMEZONE,
  userFormatFn = id,
  userTimezone = LOCAL_TIMEZONE,
}: FormatOptions = {}) {
  return function (formState: unknown): Date | null {
    if (formState instanceof Date) formState = formState.toISOString();
    if (typeof formState !== 'string') return null;

    const value = userFormatFn(formState);
    if (!value) return null;

    const time = DateTime
      // A1/B1: Assume the input string is in `serverTimezone` if it has no
      //        timezone information.
      .fromISO(value, { zone: serverTimezone })
      // B2: Convert to `timezone`, **without shifting time**.
      .setZone(userTimezone, { keepLocalTime: ignoreTimezoneInfo });

    // After all timezone conversions (order is important here), truncate the
    // time to the desired precision.
    const truncatedTime = setPrecision(time, precision);

    // A2/B3: Convert to a JS Date (B3: while preserving the displayed time).
    return truncatedTime.toJSDate();
  };
}

export interface ParseOptions {
  ignoreTimezoneInfo?: boolean;
  precision?: TimePrecision;
  serverTimezone?: string;
  userParseFn?: (value: string) => string;
}

/**
 * Converts a JS Date object from form state back into an ISO-8601
 * string (without timezone) for the API.
 *
 * This function accounts for the `ignoreTimezoneInfo` flag:
 *
 * A. If `ignoreTimezoneInfo` is **false** (or undefined):
 *    1. Receive a JS Date object, which contains system timezone info.
 *       - Example: `Date 2024-12-31T07:00:00-06:00` (system TZ = UTC-6).
 *    2. Convert to a Luxon DateTime object, keeping the JS Date's timezone.
 *       - Example: `DateTime 2024-12-31T07:00:00-06:00`.
 *    3. Convert to an ISO string in the `serverTimezone`.
 *       - Example: `"2025-01-01T00:00:00.000+11:00"`
 *
 * B. If `ignoreTimezoneInfo` is **true**:
 *    1. Receive a JS Date object, which contains system timezone info.
 *       - Example: `Date 2025-01-01T00:00:00-06:00`.
 *    2. Convert to a Luxon DateTime object, preserving the JS Date's timezone.
 *       - Example: `DateTime 2025-01-01T00:00:00-06:00`.
 *    3. Convert to `serverTimezone`, **shifting time by changing only the
 *       timezone**.
 *       - Example: If `serverTimezone = Australia/Sydney` (UTC+11), then the
 *         resulting DateTime is `2025-01-01T00:00:00+11:00` (same local time,
 *         new TZ).
 *    4. Convert to an ISO string in the `serverTimezone`.
 *       - Example: `"2025-01-01T00:00:00.000+11:00"`
 */
export function parse({
  userParseFn = id,
  ignoreTimezoneInfo = false,
  serverTimezone = SERVER_TIMEZONE,
  precision,
}: ParseOptions = {}) {
  return function (htmlValue: Date | null): unknown {
    // A1/B1: Receive a JS Date object, which contains system timezone info.
    if (!htmlValue) return null;

    const time = DateTime
      // A2/B2: Convert to a Luxon DateTime object, keeping the JS Date's timezone.
      .fromJSDate(htmlValue)
      // B3: if ignoreTimezoneInfo, convert to `serverTimezone`,
      //     **shifting time by changing only the timezone**.
      .setZone(serverTimezone, { keepLocalTime: ignoreTimezoneInfo });

    // After all timezone conversions (order is important here), truncate the
    // time to the desired precision.
    const truncatedTime = setPrecision(time, precision);

    // A3/B4: Convert to an ISO string
    return userParseFn(truncatedTime.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS"));
  };
}
